import * as React from 'react'
import { useList } from 'react-use'
import Portal from '@reach/portal'
import { motion, AnimatePresence } from 'framer-motion'

import Icon from 'components/Icon'
import {
  Alert,
  AlertAction,
  AlertContent,
  AlertIcon,
  AlertText
} from 'components/common/Alert'
import { toastContainerElement } from 'lib/toast'
import theme from 'theme'

const variationMap = {
  success: 'green',
  error: 'red',
  info: 'base'
}

const ToastContainer: React.FC = () => {
  const [timerMap] = React.useState(() => new Map())
  const [toastList, { push, updateFirst, filter }] = useList([])

  const disableToast = React.useCallback(
    function disableToast(toast) {
      filter(t => t.id !== toast.id)
    },
    [filter]
  )

  const createTimeout = React.useCallback(
    function createTimeout(toast) {
      // do not auto close
      if (toast.time < 1) return

      const timerId = setTimeout(() => {
        disableToast(toast)
      }, toast.time)

      timerMap.set(toast.id, timerId)
    },
    [disableToast, timerMap]
  )

  React.useLayoutEffect(() => {
    const listener = ({ detail: toast }: CustomEvent) => {
      push(toast)
      createTimeout(toast)
    }
    toastContainerElement.addEventListener('toast:add', listener)

    return () => {
      toastContainerElement.removeEventListener('toast:add', listener)
      timerMap.forEach((key, value) => clearTimeout(value))
    }
  }, [push, updateFirst, timerMap, createTimeout])

  return (
    <Portal>
      <AnimatePresence>
        <motion.div
          layout
          css={{
            position: 'fixed',
            bottom: 'var(--header-height)',
            right: theme.space[4],
            width: theme.space[10],
            zIndex: theme.zIndices[7],
            display: 'grid',
            gap: theme.space[2],
            paddingBottom: theme.space[2],
            scrollBarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': {
              width: 0
            }
          }}
        >
          {toastList.map(toast => {
            const toastContent = toast.text
            const testKey = toast.testKey
            const renderToastContent =
              typeof toastContent === 'function' ? (
                toastContent({
                  close: () => {
                    disableToast(toast)
                  },
                  toast
                })
              ) : (
                <AlertText>{toastContent}</AlertText>
              )

            return (
              <motion.div
                key={toast.id}
                layout
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0.5 }}
                initial={{ opacity: 0, y: 50, scale: 0.33 }}
                onMouseEnter={() => {
                  clearTimeout(timerMap.get(toast.id))
                  updateFirst(t => t.id === toast.id, {
                    ...toast,
                    time: Number.MAX_SAFE_INTEGER
                  })
                }}
                onMouseLeave={() => {
                  const updatedItem = {
                    ...toast,
                    time: Math.max(400, toast.endAt - Date.now()) // Ensure a small delay or close at original time
                  }
                  updateFirst(t => t.id === toast.id, updatedItem)
                  createTimeout(updatedItem)
                }}
              >
                <Alert
                  className={`gremlin-toast toast-${toast.type}`}
                  css={{
                    width: theme.space[10]
                  }}
                  data-test-toast={testKey}
                  variation={variationMap[toast.type]}
                  onClick={() => {
                    toast.closeOnClick && disableToast(toast)
                  }}
                >
                  <AlertIcon>
                    <Icon
                      icon={
                        toast.type === 'success'
                          ? 'checkmark'
                          : toast.type === 'error'
                          ? 'warning'
                          : 'info'
                      }
                    />
                  </AlertIcon>
                  <AlertContent>{renderToastContent}</AlertContent>
                  {toast.action && <AlertAction>{toast.action}</AlertAction>}
                </Alert>
              </motion.div>
            )
          })}
        </motion.div>
      </AnimatePresence>
    </Portal>
  )
}
export default ToastContainer
